import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Printer, Download, ChevronLeft, Scissors } from "lucide-react";
import { toPng } from "html-to-image";
import { toast } from "sonner";
import Panda from "../components/Panda";
import { useProgress } from "../context/ProgressContext";
import { STICKERS, ownsSticker, ownedCount, giftsById } from "../data/stickers";

// Letter/A4-friendly printable page: unlocked stickers with dashed cut lines, locked ones as empty outlines.
const StickerSheet = () => {
  const { activeChild, bamboo, panda } = useProgress();
  const count = ownedCount(activeChild);
  const gifts = giftsById(activeChild);
  const sheetRef = useRef(null);
  const [busy, setBusy] = useState(false);

  const download = async () => {
    if (!sheetRef.current) return;
    setBusy(true);
    try {
      const url = await toPng(sheetRef.current, { pixelRatio: 2, cacheBust: true, backgroundColor: "#ffffff", skipFonts: true });
      const a = document.createElement("a");
      a.href = url;
      a.download = `${panda?.name || "sticker"}-sticker-sheet.png`;
      a.click();
      toast.success("Sticker sheet downloaded");
    } catch (e) {
      toast.error("Could not create the image", { description: e?.message });
    } finally { setBusy(false); }
  };

  if (!activeChild) {
    return (
      <div className="max-w-3xl mx-auto px-6 py-16 text-center" data-testid="sticker-sheet-empty">
        <Panda size={120} accessory="hat" scarf="#f7931e" animate className="mx-auto" />
        <h1 className="text-3xl font-bold text-[#0c4a6e] mt-4" style={{ fontFamily: "Literata, serif" }}>Create a panda first</h1>
        <Link to="/panda" className="mt-6 inline-block btn-pill-lg bg-[#e5356f] hover:bg-[#c92a5e] text-white">Create a panda</Link>
      </div>
    );
  }

  return (
    <div className="bg-[#f7fdfe] min-h-screen pb-16" data-testid="sticker-sheet-page">
      <style>{`@media print { header, footer, .no-print { display: none !important; } body { background: #fff !important; } .sheet { box-shadow: none !important; margin: 0 !important; width: 100% !important; } @page { size: auto; margin: 12mm; } }`}</style>
      <div className="no-print max-w-4xl mx-auto px-6 pt-8 flex flex-col sm:flex-row sm:items-center gap-3">
        <Link to="/stickers" className="inline-flex items-center gap-1 font-bold text-[#1b8bc9] hover:underline" data-testid="sheet-back"><ChevronLeft size={16} /> Sticker book</Link>
        <div className="sm:ml-auto flex flex-wrap gap-2">
          <button onClick={() => window.print()} className="btn-pill bg-[#1b8bc9] hover:bg-[#166fa3] text-white inline-flex items-center gap-2" data-testid="sheet-print"><Printer size={16} /> Print</button>
          <button onClick={download} disabled={busy} className="btn-pill bg-[#f7931e] hover:bg-[#e0841a] text-white inline-flex items-center gap-2 disabled:opacity-60" data-testid="sheet-download"><Download size={16} /> {busy ? "Preparing..." : "Download PNG"}</button>
        </div>
      </div>

      <div ref={sheetRef} className="sheet bg-white mx-auto mt-6 shadow-xl rounded-lg p-8 sm:p-10" style={{ width: "min(100%, 816px)", fontFamily: "Nunito, Arial, sans-serif" }} data-testid="sticker-sheet">
        <div className="flex items-center gap-4 border-b-4 border-dotted border-[#f3c932] pb-5">
          <Panda size={84} accessory={panda.accessory} scarf={panda.scarf} />
          <div className="flex-1">
            <p className="text-[#1b8bc9] italic font-bold">Teach your child Credit</p>
            <h1 className="text-3xl sm:text-4xl font-bold text-[#0c4a6e]" style={{ fontFamily: "Literata, serif" }} data-testid="sheet-title">{panda.name}'s sticker sheet</h1>
            <p className="text-gray-600 text-sm mt-1">{count} of {STICKERS.length} stickers earned · {bamboo} bamboo · Cut along the dashed lines and stick them on your chart!</p>
          </div>
          <Scissors className="text-gray-300 hidden sm:block" size={28} />
        </div>

        <div className="mt-6 grid grid-cols-5 gap-4" data-testid="sheet-grid">
          {STICKERS.map((s, i) => {
            const unlocked = ownsSticker(activeChild, s);
            return (
              <div key={s.id} className="border-2 border-dashed rounded-2xl p-2 flex flex-col items-center text-center" style={{ borderColor: unlocked ? "#9ca3af" : "#d1d5db", background: unlocked ? "#fff" : "#fafafa" }} data-testid={`sheet-slot-${s.id}`} data-unlocked={unlocked ? "true" : "false"}>
                <div className="w-full aspect-square rounded-xl overflow-hidden flex items-center justify-center" style={{ background: unlocked ? "#fff" : "transparent" }}>
                  {unlocked ? <img src={`/stickers/${s.file}`} alt={s.name} className="w-full h-full object-cover rounded-xl" crossOrigin="anonymous" /> : <span className="text-[10px] text-gray-400 px-1 leading-tight">Earn {s.threshold} bamboo</span>}
                </div>
                <p className="mt-1.5 text-[11px] font-bold leading-tight" style={{ color: unlocked ? "#0c4a6e" : "#9ca3af" }}>{unlocked ? s.name : `#${i + 1}`}</p>
                {gifts[s.id] && <p className="text-[9px] text-[#7d5fb2] font-bold">Gift from {gifts[s.id].from_name}</p>}
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-between text-[11px] text-gray-400">
          <span>Based on the book by Ken Bossard, MBA</span>
          <span>A new sticker every 25 bamboo - keep going, {panda.name}!</span>
        </div>
      </div>
    </div>
  );
};

export default StickerSheet;
